import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import type { RecordsListFetchParams, SlaughterApiRow, SlaughterRow } from '@/types';
import { mapSlaughterRow } from '@/types';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getSlaughtersRoot: ['slaughters'],
    slaughtersWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getSlaughtersRoot, params],
};

/**
 * Every slaughter registration in the selected period and location scope, unpaged. The page derives
 * its weekly bins, facet option sets and footer totals from this one array, so there is no
 * server-side paging or filtering contract to keep in step.
 *
 * Pig type is not sent: the caller narrows `locationIds` through
 * `getSelectedSiteIdsFilteredByPigType` before handing them over.
 *
 * The wire row is reshaped to `SlaughterRow` here rather than asserted directly, the same as the
 * Deaths hook beside it - see `mapSlaughterRow` for why: keeping the reshape here isolates a later
 * wire field-name change to this call and to `mapSlaughterRow` itself.
 */
export function useSlaughters(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<SlaughterRow[]> = {}) {
    return useQuery<SlaughterRow[]>({
        queryKey: queryKeys.slaughtersWithParams(fetchParams),
        // Request behaviour goes to the constructor; `send`'s third argument is the body.
        queryFn: () =>
            new ApiRequest({ skipLoadingIndicator: true })
                .send('get', `records/slaughters?${fetchParams.value.period}&location_ids=${fetchParams.value.locationIds.join(',')}`)
                .then((rows: SlaughterApiRow[]) => rows.map(mapSlaughterRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
